// Every prop firm with rules built in (/prop-firms), one card each: its plans, account sizes and
// the kinds of rules, linking to the firm's own page. The homepage names a few firms and links
// here for the rest. The same text is sent to search engines by prerender.js.
import { Link } from 'react-router-dom';
import { PRESETS, PRESETS_CHECKED } from './balance';
import { FIRM_PAGES, firmSummary, fullName } from './firmPages';
import { PublicShell, localPath, usePageLanguage, useTitle } from './PublicPage';
import { useI18n } from './i18n';

const k = (n) => `${n / 1000}K`;

export default function PropFirmsPage({ user, forceLang }) {
  const lang = usePageLanguage(forceLang, '/prop-firms');
  const { t } = useI18n();
  useTitle(t('Prop Firm Rules Built Into Your Trading Journal | Market Matrix'));

  return (
    <PublicShell user={user} className="firm-page prop-firms-page">
      <header className="page-title firm-hero">
        <p className="eyebrow">{t('Prop firms')}</p>
        <h1>{t('Your prop firm’s rules, built in')}</h1>
        <p className="subtitle">{t('Pick your firm, plan and size when you add an account, and its rules load for you: profit target, max loss, drawdown type, daily loss limit and consistency rule. Every number stays editable.')}</p>
        <div className="firm-cta">
          <Link to={user ? '/app' : '/signup'} className="cta-button">{user ? t('Open journal') : t('Start your free journal')} &rarr;</Link>
        </div>
        {!user && <p className="cta-note">{t('Every feature free for 14 days, no credit card. The core journal stays free.')}</p>}
      </header>

      <section className="firm-section">
        <h2>{t('{n} prop firms', { n: FIRM_PAGES.length })}</h2>
        <ul className="prop-firm-grid">
          {FIRM_PAGES.map((p) => {
            const s = firmSummary(PRESETS, p.firm);
            return (
              <li key={p.slug}>
                <Link to={localPath(lang, `/${p.slug}`)} className="prop-firm-card">
                  <span className="prop-firm-name">{fullName(p)}</span>
                  <span className="prop-firm-meta">{s.plans.map((plan) => t(plan)).join(' · ')}</span>
                  <span className="prop-firm-meta">{s.minSize === s.maxSize ? k(s.minSize) : `${k(s.minSize)}–${k(s.maxSize)}`}</span>
                  <span className="prop-firm-tags">{s.rules.map((r) => <span key={r}>{t(r)}</span>)}</span>
                  <span className="prop-firm-link">{t('See the rules')} &rarr;</span>
                </Link>
              </li>
            );
          })}
        </ul>
        <p className="firm-note">
          {t('Rules last checked {month}. Prop firms change their rules often, so always confirm the current terms on your firm’s own site.', { month: t(PRESETS_CHECKED) })}
        </p>
      </section>

      <section className="firm-section">
        <h2>{t('Don’t see your firm?')}</h2>
        <p>
          {t('Add your account with your own rules: every number can be typed in, and the rules panel works the same way.')}{' '}
          <Link to="/contact">{t('Tell us which firm to add next')}</Link>
        </p>
      </section>
    </PublicShell>
  );
}
